import React, { useState } from 'react';
import { View } from 'react-native';
import { CURRENT_USER, dmChannel } from '@ventureroom/core';
import { useApp, useData } from './provider';
import {
  Avatar,
  Button,
  Card,
  Column,
  Copy,
  Empty,
  Heading,
  Input,
  Page,
  Row,
  Segments,
  Tag,
  dateLabel,
} from './components';

export function Connections() {
  const data = useData();
  const { navigate, act, busy } = useApp();
  const [tab, setTab] = useState('Network');
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const search = query.trim().toLocaleLowerCase();
  const incoming = data.connections.filter((c) => c.to === CURRENT_USER && c.status === 'pending');
  const outgoing = data.connections.filter(
    (c) => c.from === CURRENT_USER && c.status === 'pending',
  );
  const chats = data.connections
    .filter((c) => c.status === 'accepted' && [c.from, c.to].includes(CURRENT_USER))
    .flatMap((connection) => {
      const person = data.profiles.find(
        (p) => p.id === (connection.from === CURRENT_USER ? connection.to : connection.from),
      );
      if (!person || person.id === 'ai-assistant') return [];
      const last = data.messages
        .filter((m) => m.channel === dmChannel(CURRENT_USER, person.id))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
      const ventures = data.ventures.filter(
        (v) => v.members.includes(CURRENT_USER) && v.members.includes(person.id),
      );
      return [
        { person, last, ventures, updated: last?.createdAt ?? connection.createdAt, connection },
      ];
    })
    .sort((a, b) => {
      if (a.person.id === 'ai-assistant') return -1;
      if (b.person.id === 'ai-assistant') return 1;
      return b.updated.localeCompare(a.updated);
    });
  const visibleChats = chats.filter(({ person, ventures, last }) =>
    [person.name, ...ventures.map((v) => v.name), last?.text ?? ''].some((text) =>
      text.toLocaleLowerCase().includes(search),
    ),
  );
  const invitationChats = [
    ...new Map(
      data.invitations
        .filter(
          (i) =>
            i.kind === 'invitation' &&
            i.status === 'pending' &&
            [i.from, i.to].includes(CURRENT_USER),
        )
        .flatMap((i) => {
          const other = i.from === CURRENT_USER ? i.to : i.from;
          const person = data.profiles.find((p) => p.id === other);
          if (!person || chats.some((c) => c.person.id === other)) return [];
          return [
            [
              other,
              {
                person,
                invitations: data.invitations.filter(
                  (r) =>
                    r.kind === 'invitation' &&
                    r.status === 'pending' &&
                    [r.from, r.to].includes(CURRENT_USER) &&
                    [r.from, r.to].includes(other),
                ),
              },
            ] as const,
          ];
        }),
    ).values(),
  ];
  return (
    <Page
      title="Inbox"
      action={
        tab === 'Network' ? (
          <Button
            compact
            kind="quiet"
            label={searching ? 'Close chat search' : 'Search chats'}
            onPress={() => {
              setSearching(!searching);
              setQuery('');
            }}
          >
            {searching ? 'Done' : 'Search'}
          </Button>
        ) : undefined
      }
    >
      <Segments
        items={['Network', `Likes you (${incoming.length})`, `You like (${outgoing.length})`]}
        value={
          tab === 'Likes you'
            ? `Likes you (${incoming.length})`
            : tab === 'You like'
              ? `You like (${outgoing.length})`
              : tab
        }
        onChange={(value) => {
          setTab(
            value.startsWith('Likes you')
              ? 'Likes you'
              : value.startsWith('You like')
                ? 'You like'
                : value,
          );
          setSearching(false);
          setQuery('');
        }}
      />
      {tab === 'Network' && searching && (
        <Input
          label="Search chats"
          value={query}
          onChange={setQuery}
          placeholder="Name, venture or latest message"
        />
      )}
      {tab === 'Network' ? (
        <Column gap={16}>
          {!!invitationChats.length && <Heading size={18}>Venture invitations</Heading>}
          {invitationChats
            .filter(({ person, invitations }) =>
              [
                person.name,
                ...invitations.map(
                  (i) => data.ventures.find((v) => v.id === i.ventureId)?.name ?? '',
                ),
              ].some((value) => value.toLowerCase().includes(search)),
            )
            .map(({ person, invitations }) => (
              <Card
                key={person.id}
                label={`Venture invitation with ${person.name}`}
                onPress={() => navigate(`chat/${person.id}`)}
              >
                <Heading size={20}>{person.name}</Heading>
                <Copy muted>
                  {invitations
                    .map((i) => data.ventures.find((v) => v.id === i.ventureId)?.name ?? 'Venture')
                    .join(' · ')}
                </Copy>
                <Copy size={12}>Venture invitation · awaiting acceptance</Copy>
              </Card>
            ))}
          {data.profiles
            .filter(
              (p) =>
                p.subscriptionTier === 'agency' &&
                (p.id === CURRENT_USER || p.team?.includes(CURRENT_USER)),
            )
            .map((owner) => {
              const last = data.messages
                .filter((m) => m.channel === `team:${owner.id}`)
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
              const text = last
                ? `${last.author === CURRENT_USER ? 'You: ' : (data.profiles.find((p) => p.id === last.author)?.name.split(' ')[0] ?? '') + ': '}${last.text.replace(/\\s+/g, ' ')}`
                : 'Team meeting room.';
              const preview = text.length > 120 ? `${text.slice(0, 120).trimEnd()}…` : text;
              return (
                <Card
                  key={`meeting-${owner.id}`}
                  label={`${owner.name}'s Meeting Room`}
                  onPress={() => navigate(`meeting/${owner.id}`)}
                  style={{ paddingVertical: 18, borderColor: '#b91c1c' }}
                >
                  <Row style={{ alignItems: 'flex-start', gap: 14 }}>
                    <Avatar person={owner} size={52} />
                    <Column gap={5} style={{ flex: 1, minWidth: 0 }}>
                      <Row between>
                        <View style={{ flex: 1 }}>
                          <Heading size={20}>
                            {owner.id === CURRENT_USER ? 'Your' : owner.name.split(' ')[0] + "'s"}{' '}
                            Team
                          </Heading>
                        </View>
                        <Copy muted size={10}>
                          {last ? dateLabel(last.createdAt) : ''}
                        </Copy>
                      </Row>
                      <Copy bold size={12} lines={1} style={{ color: '#b91c1c' }}>
                        Meeting Room
                      </Copy>
                      <Copy muted size={13} lines={2}>
                        {preview}
                      </Copy>
                    </Column>
                    <Copy muted size={24}>
                      ›
                    </Copy>
                  </Row>
                </Card>
              );
            })}
          {visibleChats.length ? (
            visibleChats.map(({ person, last, ventures, updated, connection }) => {
              const text = last
                ? `${last.author === CURRENT_USER ? 'You: ' : ''}${last.text.replace(/\s+/g, ' ')}`
                : connection.status === 'pending'
                  ? 'Start collaborating with this person.'
                  : 'You matched. Say hello!';
              const preview = text.length > 120 ? `${text.slice(0, 120).trimEnd()}…` : text;
              return (
                <Card
                  key={person.id}
                  label={`Chat with ${person.name}`}
                  onPress={() => navigate(`chat/${person.id}`)}
                  style={{ paddingVertical: 18 }}
                >
                  <Row style={{ alignItems: 'flex-start', gap: 14 }}>
                    <Avatar person={person} size={52} />
                    <Column gap={5} style={{ flex: 1, minWidth: 0 }}>
                      <Row between>
                        <View style={{ flex: 1 }}>
                          <Heading size={20}>{person.name}</Heading>
                        </View>
                        <Copy muted size={10}>
                          {dateLabel(updated)}
                        </Copy>
                      </Row>
                      {!!ventures.length && (
                        <Copy bold size={12} lines={1}>
                          {ventures.map((v) => v.name).join(' · ')}
                        </Copy>
                      )}
                      <Copy muted size={13} lines={2}>
                        {preview}
                      </Copy>
                    </Column>
                    <Copy muted size={24}>
                      ›
                    </Copy>
                  </Row>
                </Card>
              );
            })
          ) : invitationChats.length ? null : search ? (
            <Empty
              title="No chats found"
              detail="Try another name, venture or latest message."
              action={
                <Button kind="quiet" onPress={() => setQuery('')}>
                  Clear search
                </Button>
              }
            />
          ) : (
            <Empty
              title="No matches yet"
              detail="Like someone in Discover. When they like you back, your chat appears here."
              action={<Button onPress={() => navigate('discover')}>Discover people</Button>}
            />
          )}
        </Column>
      ) : tab === 'You like' ? (
        outgoing.length ? (
          outgoing.map((c) => {
            const person = data.profiles.find((p) => p.id === c.to);
            if (!person) return null;
            return (
              <Card key={c.id}>
                <Heading size={20}>{person.name}</Heading>
                <Copy muted>{person.background}</Copy>
                <Row wrap>
                  <Button
                    kind="quiet"
                    label={`View ${person.name}'s profile`}
                    onPress={() => navigate(`person/${person.id}`)}
                  >
                    View profile
                  </Button>
                  <Button
                    kind="secondary"
                    disabled={busy}
                    onPress={() =>
                      void act({ type: 'connectionArchive', id: c.id }, 'Like removed')
                    }
                  >
                    Unlike
                  </Button>
                </Row>
              </Card>
            );
          })
        ) : (
          <Empty
            title="No outgoing likes"
            detail="People you like appear here until they like you back."
          />
        )
      ) : incoming.length ? (
        incoming.map((c) => {
          const person = data.profiles.find((p) => p.id === c.from)!;
          return (
            <Card key={c.id}>
              <Row>
                <Avatar person={person} />
                <Column gap={4}>
                  <Heading size={20}>{person.name}</Heading>
                  <Copy muted size={13}>
                    {person.background}
                  </Copy>
                </Column>
              </Row>
              <Tag>Likes you</Tag>
              {!!c.intro && <Copy>{c.intro}</Copy>}
              <Row wrap>
                <Button
                  disabled={busy}
                  onPress={async () => {
                    if (await act({ type: 'connect', personId: person.id, intro: '' }, 'Connected'))
                      navigate(`chat/${person.id}`);
                  }}
                >
                  Connect and chat
                </Button>
                <Button
                  disabled={busy}
                  kind="secondary"
                  onPress={() =>
                    void act({ type: 'bookmark', personId: person.id, state: 'passed' }, 'Passed')
                  }
                >
                  Pass
                </Button>
                <Button kind="quiet" onPress={() => navigate(`person/${person.id}`)}>
                  View profile
                </Button>
              </Row>
            </Card>
          );
        })
      ) : (
        <Empty
          title="No new likes"
          detail="When someone likes your profile, you can like them back here or in Discover."
        />
      )}
    </Page>
  );
}
